// @ts-nocheck
import useCurrencyStore from "@store/useCurrency.store";
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";

const isMobile = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

import {useTranslation} from "react-i18next";
import {BaseButton, BaseModal} from "@/components";
import LoadingLogo from "../../../../../components/LoadingLogo";
import {useBalanceStore} from "../../../../../store/useBalance.store";
import useCreateTicket from "../../hooks/useCreateTicket";
import useGetLotteryGames from "../../hooks/useGetLotteryGames";
import useGetLotteryResources from "../../hooks/useGetLotteryResources";
import {getLotteryConfig} from "../../utils/lottery.util";
import {
  findGameByStringId,
  transformDrawsToSorteos,
  transformFiguresToAnimalData,
} from "../../utils/lotteryDataTransformers";
import BoxBetsSelected from "../components/BoxBetsSelected";
import useAddTicket from "../hooks/useAddTicket";
import AnimalCards from "./components/AnimalCards";
import BetAmountModal from "./components/BetAmountModal";
import ConfirmTicketModal from "./components/ConfirmTicketModal";
import CustomNotification from "./components/CustomNotification";
import TerminalBoard from "./components/RouletteBoards/LotteryBoards/TerminalBoard/index.jsx";
import TripleBoard from "./components/RouletteBoards/LotteryBoards/TripleBoard/index.jsx";
import RouletteBoardFiftySeven from "./components/RouletteBoards/RouletteBoardFiftySeven/index.jsx";
import RouletteBoardForty from "./components/RouletteBoards/RouletteBoardForty/index.jsx";
import RouletteBoardOneOhOne from "./components/RouletteBoards/RouletteBoardOneOhOne/index.jsx";
import RouletteBoardSeventySeven from "./components/RouletteBoards/RouletteBoardSeventySeven/index.jsx";
import RouletteBoardThirtyEight from "./components/RouletteBoards/RouletteBoardThirtyEight/index.jsx";
import {TicketsBoard} from "./components/ticketsboard";
import ResultsTable from "./pages/Results/components/ResultsTable";

const lotteryLayoutCls = "flex items-center justify-center grow m-10";
const rouletteBoardLayoutCls =
  "flex gap-5 items-start justify-center grow max-md:flex-col max-md:gap-2.5 max-md:items-center max-md:justify-center max-md:w-full";
const ticketsBoardCls = "flex justify-center min-w-[240px] md:absolute md:left-0 md:z-10 max-md:w-full max-md:relative";
const rouletteBoardCls = "flex justify-end items-start grow";
const mobileFloatingButtonCls =
  "fixed bottom-0 left-0 right-0 z-[var(--z-modal)] bg-[linear-gradient(180deg,rgba(29,29,41,0.95)_0%,rgba(29,29,41,1)_100%)] border-t-2 border-[var(--color-primary)] shadow-[0_-4px_20px_rgba(0,0,0,0.5)] px-4 py-3 md:hidden";
const floatingButtonCloseCls =
  "absolute top-2 right-3 bg-transparent border-0 text-[var(--color-gray-3)] text-xl leading-none px-2 py-1 cursor-pointer transition-all duration-200 z-10 hover:text-[var(--color-primary)] hover:scale-110 active:scale-95";
const floatingButtonContentCls = "flex items-center justify-between gap-3 max-w-full";
const floatingButtonInfoCls = "flex flex-col gap-0.5 flex-1";
const floatingButtonLabelCls = "text-[0.7rem] text-[var(--color-gray-2)] uppercase tracking-[0.5px] font-semibold";
const floatingButtonAmountCls = "text-2xl font-extrabold text-[var(--color-primary)] leading-none tracking-[-0.5px]";
const floatingButtonBetsCls = "text-xs text-[var(--color-gray-3)] font-medium";
const floatingButtonActionCls =
  "!bg-[linear-gradient(135deg,var(--color-primary)_0%,var(--color-primary-hover)_100%)] !text-[var(--color-text-inverse)] !border-0 !font-bold !text-base !h-12 !px-6 !rounded-lg !shadow-[0_4px_12px_rgba(253,238,51,0.3)] uppercase tracking-[0.5px] whitespace-nowrap hover:!bg-[linear-gradient(135deg,var(--color-primary-hover)_0%,var(--color-primary)_100%)] hover:-translate-y-0.5 hover:!shadow-[0_6px_16px_rgba(253,238,51,0.4)] active:translate-y-0";

const DefaultLottery = ({gameIdString}) => {
  const {data: lotteryGamesResponse} = useGetLotteryGames();

  const apiGames = lotteryGamesResponse?.data?.data || [];
  const lotteryConfig = getLotteryConfig(apiGames);
  const game = lotteryConfig[gameIdString];

  const apiGame = findGameByStringId(apiGames, gameIdString);

  const apiSorteos = apiGame?.draws ? transformDrawsToSorteos(apiGame.draws) : [];

  const {data: resourcesData, isLoading, isSuccess, error} = useGetLotteryResources(game?.id);

  const selectedCurrency = useCurrencyStore(state => state.selectedCurrency);

  const data = {
    sorteos: apiSorteos.length > 0 ? apiSorteos : resourcesData?.sorteos || [],
    fichas: resourcesData?.fichas || [],
  };

  const [selectedSorteo, setSelectedSorteo] = useState(null);

  const [betAmount, setBetAmount] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isBetModalVisible, setIsBetModalVisible] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // New state for improved betting flow
  const [selectedAnimalForBet, setSelectedAnimalForBet] = useState(null);
  const [lastBetAmount, setLastBetAmount] = useState(null);

  const balance = useBalanceStore(state => state.getBalance);

  const [animalsSelected, setAnimalsSelected] = useState([]);

  const [placedBets, setPlacedBets] = useState([]);

  const [selectedFicha, setSelectedFicha] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const [isDeletionMode, setIsDeletionMode] = useState(false);

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  // State for mobile floating button visibility
  const [isFloatingButtonVisible, setIsFloatingButtonVisible] = useState(true);

  const lastFichaPlacementRef = useRef(0);

  const {addTicket} = useAddTicket();

  const {postCreateTicket, isPending, isSuccess: isTicketCreated, error: ticketError} = useCreateTicket();

  const {t} = useTranslation();

  useEffect(() => {
    if (data?.sorteos?.length > 0 && !selectedSorteo) {
      setSelectedSorteo(data.sorteos[0]?.id);
    }
  }, [data?.sorteos, selectedSorteo]);

  useEffect(() => {
    setPlacedBets([]);
    setAnimalsSelected([]);
    setSelectedFicha(null);
    setSelectedTime(null);
    setIsDeletionMode(false);
    setTickets([]);

    setIsBetModalVisible(false);
    setIsConfirmModalVisible(false);
  }, [gameIdString, game?.id]);

  useEffect(() => {
    if (isTicketCreated && isConfirmModalVisible) {
      setTickets([]);
    }
  }, [isTicketCreated, isConfirmModalVisible]);

  useEffect(() => {
    if (ticketError) {
      setIsDeletionMode(false);
      setTickets([]);
      setIsConfirmModalVisible(false);
    }
  }, [ticketError]);

  const handleAnimalChange = ficha => {
    setAnimalsSelected([ficha]);
    showBetModal();
  };

  const getNumbersForGroupBet = (betType, value) => {
    let animalData = [];

    if (apiGame?.figures && apiGame.figures.length > 0) {
      animalData = transformFiguresToAnimalData(apiGame.figures);
    } else {
      animalData = [
        {number: 1, name: "carnero", color: "red"},
        {number: 2, name: "toro", color: "dark"},
        {number: 3, name: "ciempies", color: "red"},
        {number: 4, name: "alacran", color: "dark"},
        {number: 5, name: "leon", color: "red"},
        {number: 6, name: "rana", color: "dark"},
        {number: 7, name: "perico", color: "red"},
        {number: 8, name: "raton", color: "dark"},
        {number: 9, name: "aguila", color: "red"},
        {number: 10, name: "tigre", color: "dark"},
        {number: 11, name: "gato", color: "dark"},
        {number: 12, name: "caballo", color: "red"},
        {number: 13, name: "mono", color: "dark"},
        {number: 14, name: "paloma", color: "red"},
        {number: 15, name: "zorro", color: "dark"},
        {number: 16, name: "oso", color: "red"},
        {number: 17, name: "pavo", color: "dark"},
        {number: 18, name: "burro", color: "red"},
        {number: 19, name: "chivo", color: "red"},
        {number: 20, name: "cochino", color: "dark"},
        {number: 21, name: "gallo", color: "red"},
        {number: 22, name: "camellos", color: "dark"},
        {number: 23, name: "cebra", color: "red"},
        {number: 24, name: "iguana", color: "dark"},
        {number: 25, name: "gallina", color: "red"},
        {number: 26, name: "vaca", color: "dark"},
        {number: 27, name: "perro", color: "red"},
        {number: 28, name: "zamuro", color: "dark"},
        {number: 29, name: "elefante", color: "dark"},
        {number: 30, name: "caiman", color: "red"},
        {number: 31, name: "lapa", color: "dark"},
        {number: 32, name: "ardilla", color: "red"},
        {number: 33, name: "pescado", color: "red"},
        {number: 34, name: "venado", color: "red"},
        {number: 35, name: "jirafa", color: "dark"},
        {number: 36, name: "culebra", color: "red"},
      ];
    }

    const numbers = [];

    switch (betType) {
      case "range":
        if (value === "1-18") {
          for (let i = 1; i <= 18; i++) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        } else if (value === "19-36") {
          for (let i = 19; i <= 36; i++) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        }
        break;

      case "evenOdd":
        if (value === "PAR") {
          animalData.forEach(animal => {
            if (animal.number % 2 === 0) {
              numbers.push({id: animal.number, name: animal.name});
            }
          });
        } else if (value === "IMPAR") {
          animalData.forEach(animal => {
            if (animal.number % 2 !== 0) {
              numbers.push({id: animal.number, name: animal.name});
            }
          });
        }
        break;

      case "color":
        if (value === "ROJO") {
          animalData.forEach(animal => {
            if (animal.color === "red") {
              numbers.push({id: animal.number, name: animal.name});
            }
          });
        } else if (value === "NEGRO") {
          animalData.forEach(animal => {
            if (animal.color === "dark") {
              numbers.push({id: animal.number, name: animal.name});
            }
          });
        }
        break;

      case "dozen":
        if (value === "1-12") {
          for (let i = 1; i <= 12; i++) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        } else if (value === "13-24") {
          for (let i = 13; i <= 24; i++) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        } else if (value === "25-36") {
          for (let i = 25; i <= 36; i++) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        }
        break;

      case "column":
        if (value === "2-1-col1") {
          for (let i = 1; i <= 34; i += 3) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        } else if (value === "2-1-col2") {
          for (let i = 2; i <= 35; i += 3) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        } else if (value === "2-1-col3") {
          for (let i = 3; i <= 36; i += 3) {
            const animal = animalData.find(a => a.number === i);
            numbers.push({id: i, name: animal?.name || `Animal ${i}`});
          }
        }
        break;

      default:
        break;
    }

    return numbers;
  };

  const handleRouletteBet = betData => {
    // First, check if user needs to select a time
    if (!selectedTime || !selectedSorteo) {
      CustomNotification.error("Selecciona una hora de sorteo primero");
      return;
    }

    // If in deletion mode, handle deletion
    if (isDeletionMode) {
      handleDeleteBetFromOption(betData.value);
      setIsDeletionMode(false);
      setSelectedFicha(null);
      return;
    }

    // Store the selected animal and show modal to choose bet amount
    setSelectedAnimalForBet(betData);
    // Pre-fill with last bet amount if available
    if (lastBetAmount) {
      setBetAmount(lastBetAmount);
    }
    setIsBetModalVisible(true);
  };

  /**
   * Confirms the bet after user selects amount in modal
   */
  const handleConfirmBet = amount => {
    if (!amount || amount <= 0) {
      CustomNotification.error("Ingresa un monto vÃ¡lido");
      return;
    }

    if (!selectedAnimalForBet) {
      CustomNotification.error("No hay animal seleccionado");
      return;
    }

    // Remember this amount for next bets
    setLastBetAmount(amount);

    const now = Date.now();
    if (lastFichaPlacementRef.current && now - lastFichaPlacementRef.current < 300) {
      return;
    }
    lastFichaPlacementRef.current = now;

    const groupedBetTypes = ["range", "evenOdd", "color", "dozen", "column"];

    if (groupedBetTypes.includes(selectedAnimalForBet.betType)) {
      const targetNumbers = getNumbersForGroupBet(selectedAnimalForBet.betType, selectedAnimalForBet.value);

      if (targetNumbers.length > 0) {
        targetNumbers.forEach(target => {
          placeBetWithAmount(target.id, target.name, amount, true);
        });

        placeBetWithAmount(
          selectedAnimalForBet.value,
          selectedAnimalForBet.name || `${selectedAnimalForBet.betType}-${selectedAnimalForBet.value}`,
          amount,
          true,
          true
        );

        CustomNotification.success(
          `Apuesta grupal colocada: $${amount} en ${targetNumbers.length} opciones (Total: $${
            amount * targetNumbers.length
          })`
        );
      }
    } else {
      placeBetWithAmount(
        selectedAnimalForBet.value,
        selectedAnimalForBet.name || `${selectedAnimalForBet.betType}-${selectedAnimalForBet.value}`,
        amount,
        false,
        false
      );
    }

    // Close modal and reset
    setIsBetModalVisible(false);
    setSelectedAnimalForBet(null);
  };

  /**
   * Places a bet with a specific amount (replaces the old ficha-based system)
   */
  const placeBetWithAmount = (
    animalNumber,
    animalName,
    amount,
    skipTotalLimitCheck = false,
    isGroupBetVisual = false
  ) => {
    if (!amount || amount <= 0) {
      if (!skipTotalLimitCheck) {
        CustomNotification.warning("Ingresa un monto vÃ¡lido");
      }
      return;
    }

    if (!selectedTime || !selectedSorteo) {
      if (!skipTotalLimitCheck) {
        CustomNotification.error("Selecciona una hora de sorteo primero");
      }
      return;
    }

    const actualAnimalId = animalNumber;
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newFicha = {
      id: uniqueId,
      value: amount,
      name: `bet-${amount}`,
    };

    setPlacedBets(prevBets => {
      const fichaExists = prevBets.some(bet => bet.fichas.some(ficha => ficha.id === uniqueId));

      if (fichaExists) {
        return prevBets;
      }

      const existingBetIndex = prevBets.findIndex(bet => bet.animalId === actualAnimalId);

      if (existingBetIndex >= 0) {
        const updatedBets = [...prevBets];
        updatedBets[existingBetIndex].fichas = [...updatedBets[existingBetIndex].fichas, newFicha];
        return updatedBets;
      }
      return [
        ...prevBets,
        {
          animalId: actualAnimalId,
          animalName,
          fichas: [newFicha],
          isGroupBetVisual,
        },
      ];
    });

    // Show floating button when a new bet is placed
    setIsFloatingButtonVisible(true);

    if (!skipTotalLimitCheck) {
      CustomNotification.success(`Apuesta de $${amount} colocada en ${animalName}`);
    }
  };

  const handleFichaSelect = ficha => {
    if (ficha === null) {
      if (isDeletionMode) {
        setIsDeletionMode(false);
        CustomNotification.warning("Modo borrar cancelado");
      } else {
        if (placedBets.length === 0) {
          CustomNotification.warning("No hay fichas para borrar");
          return;
        }
        setIsDeletionMode(true);
        setSelectedFicha(null);
        CustomNotification.warning("Modo borrar activado - Haz clic en la apuesta que deseas eliminar");
      }
    } else {
      if (isDeletionMode) {
        setIsDeletionMode(false);
      }
      setSelectedFicha(ficha);
    }
  };

  const handleDeleteBetFromOption = optionId => {
    const groupedOptions = {
      "1-18": {type: "range", value: "1-18"},
      "19-36": {type: "range", value: "19-36"},
      PAR: {type: "evenOdd", value: "PAR"},
      IMPAR: {type: "evenOdd", value: "IMPAR"},
      ROJO: {type: "color", value: "ROJO"},
      NEGRO: {type: "color", value: "NEGRO"},
      "1-12": {type: "dozen", value: "1-12"},
      "13-24": {type: "dozen", value: "13-24"},
      "25-36": {type: "dozen", value: "25-36"},
      "2-1-col1": {type: "column", value: "2-1-col1"},
      "2-1-col2": {type: "column", value: "2-1-col2"},
      "2-1-col3": {type: "column", value: "2-1-col3"},
    };

    if (groupedOptions[optionId]) {
      const groupInfo = groupedOptions[optionId];
      const targetNumbers = getNumbersForGroupBet(groupInfo.type, groupInfo.value);

      let totalDeleted = 0;

      setPlacedBets(prevBets => {
        const idsToDelete = [optionId, ...targetNumbers.map(n => n.id)];
        idsToDelete.forEach(id => {
          const bet = prevBets.find(b => b.animalId === id);
          if (bet) {
            totalDeleted += bet.fichas.reduce((sum, ficha) => sum + ficha.value, 0);
          }
        });

        return prevBets.filter(bet => !idsToDelete.includes(bet.animalId));
      });

      if (totalDeleted > 0) {
        CustomNotification.success(`Apuestas eliminadas: $${totalDeleted}`);
      } else {
        CustomNotification.warning("No hay apuestas en esta opciÃ³n");
      }
    } else {
      const betToDelete = placedBets.find(bet => bet.animalId === optionId);

      if (!betToDelete) {
        CustomNotification.warning("No hay apuestas en esta opciÃ³n");
        return;
      }

      setPlacedBets(prevBets => prevBets.filter(bet => bet.animalId !== optionId));

      const totalDeleted = betToDelete.fichas.reduce((sum, ficha) => sum + ficha.value, 0);
      CustomNotification.success(`Apuestas eliminadas: $${totalDeleted}`);
    }
  };

  const handleTimeSelect = (time, sorteoId) => {
    setSelectedTime(time);

    if (sorteoId) {
      setSelectedSorteo(sorteoId);
    } else {
      const foundSorteo = data.sorteos?.find(s => s.hora === time);
      setSelectedSorteo(foundSorteo?.id || null);
    }
  };

  const handleClearBets = () => {
    setTickets([]);
    setAnimalsSelected([]);
    setPlacedBets([]);
    setBetAmount(null);
    setSelectedFicha(null);
    CustomNotification.warning(t("Las apuestas han sido borradas"));
  };

  const handleCloseFloatingButton = () => {
    setIsFloatingButtonVisible(false);
  };

  const handleRemoveFicha = (animalId, fichaId) => {
    setPlacedBets(prevBets => {
      return prevBets
        .map(bet => {
          if (bet.animalId === animalId) {
            const updatedFichas = bet.fichas.filter(ficha => ficha.id !== fichaId);
            return {...bet, fichas: updatedFichas};
          }
          return bet;
        })
        .filter(bet => bet.fichas.length > 0);
    });
    CustomNotification.warning("Ficha removida");
  };

  const handleConfirmPurchase = () => {
    if (placedBets.length > 0) {
      convertPlacedBetsToTickets();
    } else if (tickets.length > 0) {
      handlePlay();
    }
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalVisible(false);

    setPlacedBets([]);
  };

  const handleCloseModalKeepBets = () => {
    setIsConfirmModalVisible(false);
  };

  const handleRemoveBetItem = (betIndex, fichaIndex) => {
    setPlacedBets(prevBets => {
      const updatedBets = [...prevBets];
      const bet = updatedBets[betIndex];

      if (fichaIndex === null) {
        updatedBets.splice(betIndex, 1);
        return updatedBets;
      }

      if (bet && bet.fichas.length > fichaIndex) {
        bet.fichas.splice(fichaIndex, 1);

        if (bet.fichas.length === 0) {
          updatedBets.splice(betIndex, 1);
        }
      }

      return updatedBets;
    });
  };

  const handleBuyTicket = () => {
    if (placedBets.length === 0 && tickets.length === 0) {
      CustomNotification.error("No hay apuestas para comprar");
      return;
    }

    if (!selectedTime || !selectedSorteo) {
      CustomNotification.error("Selecciona una hora de sorteo primero");
      return;
    }

    setIsConfirmModalVisible(true);
  };

  const convertPlacedBetsToTickets = () => {
    if (!selectedSorteo) {
      CustomNotification.error("Selecciona un sorteo primero");
      return;
    }

    const actualBets = placedBets.filter(bet => !bet.isGroupBetVisual);

    const totalAmount = actualBets.reduce((total, bet) => {
      return total + bet.fichas.reduce((sum, ficha) => sum + ficha.value, 0);
    }, 0);

    const jugadas = actualBets.map(bet => {
      let backendFigureId = null;
      let animalNumber = bet.animalId;

      if (apiGame?.figures && Array.isArray(apiGame.figures)) {
        const matchingFigure = apiGame.figures.find(figure => {
          return String(figure.number) === String(bet.animalId);
        });

        if (matchingFigure) {
          backendFigureId = matchingFigure.id;
          animalNumber = matchingFigure.number;
        } else {
        }
      }

      const betTotalAmount = bet.fichas.reduce((sum, ficha) => sum + ficha.value, 0);

      return {
        monto: betTotalAmount,
        numero: animalNumber,
        sign_id: backendFigureId,
      };
    });

    const newTicket = {
      id: selectedSorteo,
      hora: data.sorteos.find(s => s.id === selectedSorteo)?.hora || "",
      loteria_id: game.id,
      totalAmountTicket: totalAmount,
      jugadas: jugadas,
    };

    setTickets(prevTickets => {
      const updatedTickets = [...prevTickets, newTicket];

      setTimeout(() => {
        handlePlayWithTickets(updatedTickets);
      }, 0);

      return updatedTickets;
    });
  };

  const MIN_BET_AMOUNT = 15;
  const MAX_BET_AMOUNT = 45;
  const MAX_BET_PER_OPTION = 1000;

  const handleMaxBetReached = (_optionValue, maxLimit) => {
    CustomNotification.warning(
      `LÃ­mite mÃ¡ximo alcanzado para esta opciÃ³n (${maxLimit}). No se pueden agregar mÃ¡s fichas aquÃ­.`
    );
  };

  const [betAmountError, setBetAmountError] = useState(null);

  const handleBetAmountChange = value => {
    setBetAmount(value);

    setBetAmountError(null);

    if (value === null || value === undefined || value === "") {
      setBetAmountError(t("lotteryErrorMinBetAmount", {min: MIN_BET_AMOUNT}));
    } else if (value < MIN_BET_AMOUNT) {
      setBetAmountError(t("lotteryErrorMinBetAmount", {min: MIN_BET_AMOUNT}));
    } else if (value > MAX_BET_AMOUNT) {
      setBetAmountError(t("lotteryErrorMaxBetAmount", {max: MAX_BET_AMOUNT}));
    }
  };

  const isBetAmountValid =
    betAmount !== null &&
    betAmount !== undefined &&
    betAmount !== "" &&
    betAmount >= MIN_BET_AMOUNT &&
    betAmount <= MAX_BET_AMOUNT;

  const isFormValid = selectedSorteo && animalsSelected.length > 0 && isBetAmountValid;

  const handleAddTicket = () => {
    if (!isFormValid) {
      CustomNotification.error(t("lotteryErrorSelectDrawAnimal"));
      return;
    }

    setIsBetModalVisible(false);
    const newTicket = addTicket({
      selectedSorteo,
      animalsSelected,
      betAmount,
      tickets,
      data,
      game,
    });

    if (newTicket) {
      setTickets(newTicket);
      setAnimalsSelected([]);
      setBetAmount(null);
      setBetAmountError(null);
    }
  };
  const handlePlay = () => {
    handlePlayWithTickets(tickets);
  };

  const handlePlayWithTickets = ticketsToSend => {
    if (ticketsToSend.length === 0 && placedBets.length === 0) {
      CustomNotification.error(t("lotteryErrorInput"));
      return;
    }

    if (balance < totalBetAmount) {
      CustomNotification.error(t("lotteryErrorBalance"));

      return;
    }

    const items = ticketsToSend.map(ticket => {
      const draw_id = ticket.id || ticket.draw_id || ticket.sorteo_id || ticket.loteria_id;

      const bets = (ticket.jugadas || []).map(jugada => {
        const isAnimalGame = apiGame?.game_type === "animals" || apiGame?.game_type === "Animalitos";
        const animalNumber = jugada.numero || jugada.number;

        const amount = parseFloat(jugada.monto || jugada.amount || 0);

        if (isAnimalGame) {
          return {
            numbers: [],
            amount: amount,
            sign_id: String(animalNumber),
          };
        } else {
          // Pad number to 3 digits with leading zeros (e.g., 1 -> "001", 99 -> "099")
          const paddedNumber = animalNumber ? String(animalNumber).padStart(3, "0") : null;
          return {
            numbers: paddedNumber ? [paddedNumber] : [],
            amount: amount,
            sign_id: null,
          };
        }
      });

      return {
        draw_id,
        bets,
      };
    });

    const payload = {
      currency: "VES",
      items,
    };

    postCreateTicket(payload);
  };

  const handleShowResults = () => {
    setShowResults(!showResults);
  };

  const showBetModal = () => {
    setIsBetModalVisible(true);
  };

  const handleBetModalCancel = () => {
    setIsBetModalVisible(false);
    setAnimalsSelected([]);
    setBetAmount(null);
    setSelectedAnimalForBet(null);
  };

  const ticketAmount = (tickets || []).reduce((total, ticket) => {
    return total + ticket.jugadas.reduce((sum, jugada) => sum + jugada.monto, 0);
  }, 0);

  const placedBetsAmount = placedBets
    .filter(bet => !bet.isGroupBetVisual)
    .reduce((total, bet) => {
      return total + bet.fichas.reduce((sum, ficha) => sum + ficha.value, 0);
    }, 0);

  const totalBetAmount = ticketAmount + placedBetsAmount;

  const renderBoardContent = () => {
    const commonProps = {
      gameIdString,
      onBetClick: handleRouletteBet,
      placedBets,
      onRemoveFicha: handleRemoveFicha,
      maxBetPerOption: MAX_BET_PER_OPTION,
      onMaxBetReached: handleMaxBetReached,
      isDeletionMode,
    };

    switch (game.boardType) {
      case "one-oh-one":
        return (
          <RouletteBoardOneOhOne
            {...commonProps}
            figures={apiGame?.figures || []}
          />
        );
      case "fifty-seven":
        return (
          <RouletteBoardFiftySeven
            {...commonProps}
            figures={apiGame?.figures || []}
          />
        );
      case "seventy-seven":
        return (
          <RouletteBoardSeventySeven
            {...commonProps}
            figures={apiGame?.figures || []}
          />
        );
      case "forty":
        return (
          <RouletteBoardForty
            {...commonProps}
            figures={apiGame?.figures || []}
          />
        );
      case "thirty-eight":
        return (
          <RouletteBoardThirtyEight
            {...commonProps}
            figures={apiGame?.figures || []}
          />
        );
      case "triple":
        return (
          <TripleBoard
            gameIdString={gameIdString}
            gameName={apiGame?.name || ""}
            onBetClick={handleRouletteBet}
            placedBets={placedBets}
            maxBetPerOption={MAX_BET_PER_OPTION}
            onMaxBetReached={handleMaxBetReached}
            isDeletionMode={isDeletionMode}
            draws={data?.sorteos || []}
          />
        );
      case "terminal":
        return (
          <TerminalBoard
            gameIdString={gameIdString}
            gameName={apiGame?.name || ""}
            onBetClick={handleRouletteBet}
            placedBets={placedBets}
            maxBetPerOption={MAX_BET_PER_OPTION}
            onMaxBetReached={handleMaxBetReached}
            isDeletionMode={isDeletionMode}
            draws={data?.sorteos || []}
          />
        );
      default:
        return (
          <AnimalCards
            fichas={data.fichas || []}
            selectedAnimals={animalsSelected}
            onAnimalClick={handleAnimalChange}
          />
        );
    }
  };

  const ticketsBoardProps = {
    totalAmount: totalBetAmount,
    onClearBets: handleClearBets,
    onFichaSelect: handleFichaSelect,
    selectedFicha,
    onTimeSelect: handleTimeSelect,
    selectedTime,
    onBuyTicket: handleBuyTicket,
    placedBets,
    sorteos: data?.sorteos || [],
    isDeletionMode,
    hideChips: true, // Hide chips in the new betting flow
  };

  if (isLoading || !isSuccess || !game) {
    return <LoadingLogo />;
  }

  if (error) {
    return (
      <div>
        {t("lotteryErrorLoadingData")}: {error.message}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "0 0" : gameIdString === "ruleta-activa" ? "0" : "0 16px",
      }}
    >
      <div style={{display: "flex", justifyContent: "center", margin: "20px 0"}}>
        <BaseButton
          variant="primary"
          onClick={handleShowResults}
        >
          {showResults ? t("lotteryHideResults") : t("lotteryShowResults")}
        </BaseButton>
      </div>

      {showResults && apiGame?.id && (
        <ResultsTable
          gameId={apiGame.id}
          figures={apiGame?.figures || []}
        />
      )}

      {!showResults && (
        <div className={lotteryLayoutCls}>
          <div className={rouletteBoardLayoutCls}>
            <div className={ticketsBoardCls}>
              {game.boardType !== "triple" && game.boardType !== "terminal" && <TicketsBoard {...ticketsBoardProps} />}
            </div>
            <div className={rouletteBoardCls}>{renderBoardContent()}</div>
          </div>
        </div>
      )}

      {/* Mobile Floating Purchase Button - Only for animalitos games */}
      {!showResults &&
        isMobile &&
        isFloatingButtonVisible &&
        (game.boardType === "thirty-eight" ||
          game.boardType === "forty" ||
          game.boardType === "fifty-seven" ||
          game.boardType === "seventy-seven" ||
          game.boardType === "one-oh-one") &&
        placedBets.length > 0 && (
          <div className={mobileFloatingButtonCls}>
            <button
              type="button"
              className={floatingButtonCloseCls}
              onClick={handleCloseFloatingButton}
              aria-label="Cerrar"
            >
              âœ•
            </button>
            <div className={floatingButtonContentCls}>
              <div className={floatingButtonInfoCls}>
                <span className={floatingButtonLabelCls}>Total:</span>
                <span className={floatingButtonAmountCls}>
                  {selectedCurrency?.symbol || "Bs"} {totalBetAmount}
                </span>
                <span className={floatingButtonBetsCls}>({placedBets.length} apuestas)</span>
              </div>
              <BaseButton
                variant="primary"
                onClick={handleBuyTicket}
                className={floatingButtonActionCls}
              >
                Comprar Ticket
              </BaseButton>
            </div>
          </div>
        )}

      {/* New Bet Amount Modal for roulette-style betting */}
      <BetAmountModal
        visible={isBetModalVisible && selectedAnimalForBet !== null}
        onConfirm={handleConfirmBet}
        onCancel={handleBetModalCancel}
        animalName={selectedAnimalForBet?.name || selectedAnimalForBet?.value}
        initialAmount={lastBetAmount}
      />

      {/* Old modal for card-based games (kept for backward compatibility) */}
      <BaseModal
        title={t("lotteryModalInput")}
        open={isBetModalVisible && selectedAnimalForBet === null}
        onClose={handleBetModalCancel}
        footer={
          <>
            <BaseButton
              variant="secondary"
              className="btn-secondary-hover"
              onClick={handleBetModalCancel}
            >
              {t("lotteryModalCancelBet")}
            </BaseButton>
            <BaseButton
              variant="primary"
              className={`btn-tertiary-hover ${!isFormValid ? "disabled" : ""}`}
              disabled={!isFormValid}
              onClick={isFormValid ? handleAddTicket : undefined}
            >
              {t("lotteryModalAddBet")}
            </BaseButton>
          </>
        }
      >
        <BoxBetsSelected
          animalsSelected={animalsSelected}
          betAmount={betAmount}
          handleBetAmountChange={handleBetAmountChange}
          betAmountError={betAmountError}
          isBetAmountValid={isBetAmountValid}
          minBetAmount={MIN_BET_AMOUNT}
          maxBetAmount={MAX_BET_AMOUNT}
        />
      </BaseModal>
      {}
      <ConfirmTicketModal
        visible={isConfirmModalVisible}
        onClose={handleCloseConfirmModal}
        onCloseKeepBets={handleCloseModalKeepBets}
        placedBets={placedBets}
        totalAmount={totalBetAmount}
        selectedTime={selectedTime}
        onConfirmPurchase={handleConfirmPurchase}
        onRemoveBet={handleRemoveBetItem}
        isPending={isPending}
        isSuccess={isTicketCreated}
        isError={!!ticketError}
        gameType={game?.boardType === "triple" || game?.boardType === "terminal" ? "numbers" : "animals"}
        gameName={apiGame?.name || ""}
      />
    </div>
  );
};

DefaultLottery.propTypes = {
  gameIdString: PropTypes.string.isRequired,
};

export default DefaultLottery;
